import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  Modal,
  NumberInput,
  Progress,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, unwrap } from '../../api/client';
import type { CurrencyRule } from '../../api/types';
import { useSession } from '../../app/session';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { applyFieldErrors, notifyError, notifySaved } from '../../lib/notify';

/**
 * Whether this pilot is in currency (u trenaži), and what would put them back.
 *
 * The rules come from the club defaults for the pilot's status plus any personal override, and
 * every one is shown with the measured value against its threshold so a decision can be
 * checked rather than trusted.
 */
export function CurrencyTab({ personId }: { personId: number }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const [editing, setEditing] = useState<CurrencyRule | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);

  const currency = useQuery({
    queryKey: ['currency', personId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/people/{id}/currency/', { params: { path: { id: personId } } }),
      ),
  });

  const rules = useQuery({
    queryKey: ['currency-rules', personId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/currency-rules/', {
          params: { query: { person: personId, page_size: 100 } },
        }),
      ),
  });

  if (currency.isLoading) return <Loader />;

  const overview = currency.data;
  const personalRuleIds = new Set((rules.data?.results ?? []).map((rule) => rule.id));

  return (
    <Stack gap="lg">
      {overview?.blocking?.length ? (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
          <Stack gap={4}>
            <Text fw={600} fz="sm">
              {t('staff.blockingItems')}
            </Text>
            {overview.blocking.map((item, index) => (
              <Text key={index} fz="sm">
                {item.message}
              </Text>
            ))}
          </Stack>
        </Alert>
      ) : (
        <Alert color="teal" variant="light">
          {overview?.is_current ? t('staff.isCurrent') : t('staff.notCurrent')}
        </Alert>
      )}

      <Card p="md">
        <Group justify="space-between" mb="sm">
          <Text fw={600}>{t('staff.currency')}</Text>
          {can('staff.manage_currency') ? (
            <Button
              variant="light"
              size="compact-sm"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              {t('staff.addCurrencyRule')}
            </Button>
          ) : null}
        </Group>

        {overview?.rules?.length ? (
          <Table fz="sm" verticalSpacing={8}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('staff.currencyRule')}</Table.Th>
                <Table.Th w={150}>{t('staff.measured')}</Table.Th>
                <Table.Th w={110}>{t('staff.threshold')}</Table.Th>
                <Table.Th w={120}>{t('common.status')}</Table.Th>
                <Table.Th w={90} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {overview.rules.map((rule) => (
                <Table.Tr key={rule.rule_id}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Text fz="sm">{rule.label}</Text>
                      {rule.blocks_flying ? (
                        <Badge size="xs" color="red" variant="light">
                          {t('staff.blockingItems')}
                        </Badge>
                      ) : null}
                      <Badge size="xs" variant="outline" color="gray">
                        {personalRuleIds.has(rule.rule_id)
                          ? t('staff.personalOverride')
                          : t('staff.clubDefault')}
                      </Badge>
                    </Group>
                    <Progress
                      mt={6}
                      size="xs"
                      value={ruleProgress(rule.value, rule.threshold)}
                      color={
                        rule.status === 'EXPIRED'
                          ? 'red'
                          : rule.status === 'EXPIRING'
                            ? 'orange'
                            : 'teal'
                      }
                    />
                  </Table.Td>
                  <Table.Td ff="monospace">{rule.value ?? '—'}</Table.Td>
                  <Table.Td ff="monospace">{rule.threshold}</Table.Td>
                  <Table.Td>
                    <StatusBadge kind="validity" value={rule.status} size="sm" />
                  </Table.Td>
                  <Table.Td>
                    {can('staff.manage_currency') && personalRuleIds.has(rule.rule_id) ? (
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        onClick={() => {
                          const match = (rules.data?.results ?? []).find(
                            (item) => item.id === rule.rule_id,
                          );
                          setEditing(match ?? null);
                          setFormOpen(true);
                        }}
                      >
                        {t('action.edit')}
                      </Button>
                    ) : null}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <EmptyState title={t('staff.noCurrencyRules')} />
        )}
      </Card>

      {overview?.expiring_documents?.length ? (
        <Card p="md">
          <Text fw={600} mb="sm">
            {t('staff.expiringDocuments')}
          </Text>
          <Table fz="sm" verticalSpacing={6}>
            <Table.Tbody>
              {overview.expiring_documents.map((document) => (
                <Table.Tr key={`${document.kind}-${document.id}`}>
                  <Table.Td w={120}>
                    <StatusBadge kind="validity" value={document.status} size="sm" />
                  </Table.Td>
                  <Table.Td>{document.name}</Table.Td>
                  <Table.Td w={110} ff="monospace">
                    {document.valid_until}
                  </Table.Td>
                  <Table.Td w={110} ta="right" c="dimmed">
                    {document.days_remaining} {t('common.days')}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      ) : null}

      <CurrencyRuleModal
        opened={isFormOpen}
        onClose={() => setFormOpen(false)}
        personId={personId}
        rule={editing}
      />
    </Stack>
  );
}

/** How close the measured value sits to its threshold, as a bar rather than a number. */
function ruleProgress(value: string | null | undefined, threshold: string) {
  const measured = Number(value ?? 0);
  const limit = Number(threshold);
  if (!Number.isFinite(measured) || !Number.isFinite(limit) || limit === 0) return 0;
  return Math.min(100, Math.round((measured / limit) * 100));
}

function CurrencyRuleModal({
  opened,
  onClose,
  personId,
  rule,
}: {
  opened: boolean;
  onClose: () => void;
  personId: number;
  rule: CurrencyRule | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enumChoices } = useSession();

  const form = useForm({
    initialValues: {
      label: '',
      metric: 'DAYS_SINCE_LAST_FLIGHT',
      comparison: 'AT_MOST',
      threshold: 30,
      warn_margin: 7,
      discipline: '',
      aircraft_category: '',
      blocks_flying: true,
      is_active: true,
    },
    validate: {
      label: (value) => (value.trim() ? null : t('common.requiredField')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      label: rule?.label ?? '',
      metric: rule?.metric ?? 'DAYS_SINCE_LAST_FLIGHT',
      comparison: rule?.comparison ?? 'AT_MOST',
      threshold: rule ? Number(rule.threshold) : 30,
      warn_margin: rule ? Number(rule.warn_margin ?? 0) : 7,
      discipline: rule?.discipline ?? '',
      aircraft_category: rule?.aircraft_category ?? '',
      blocks_flying: rule?.blocks_flying ?? true,
      is_active: rule?.is_active ?? true,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, rule?.id]);

  const save = useMutation({
    mutationFn: async (values: typeof form.values) => {
      const body = {
        person: personId,
        label: values.label.trim(),
        metric: values.metric as CurrencyRule['metric'],
        comparison: values.comparison as CurrencyRule['comparison'],
        threshold: String(values.threshold),
        warn_margin: String(values.warn_margin),
        discipline: values.discipline as CurrencyRule['discipline'],
        aircraft_category: values.aircraft_category as CurrencyRule['aircraft_category'],
        blocks_flying: values.blocks_flying,
        is_active: values.is_active,
      };
      if (rule) {
        return unwrap(
          await api.PATCH('/api/v1/currency-rules/{id}/', {
            params: { path: { id: rule.id } },
            body,
          }),
        );
      }
      return unwrap(await api.POST('/api/v1/currency-rules/', { body }));
    },
    onSuccess: async () => {
      notifySaved();
      await queryClient.invalidateQueries({ queryKey: ['currency'] });
      await queryClient.invalidateQueries({ queryKey: ['currency-rules'] });
      onClose();
    },
    onError: (error: ApiError) => {
      if (!applyFieldErrors(error, form.setErrors)) notifyError(error);
    },
  });

  return (
    <Modal opened={opened} onClose={onClose} title={t('staff.currencyRule')} size="lg">
      <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
        <Stack gap="md">
          <TextInput
            label={t('common.name')}
            required
            placeholder={t('staff.currencyRule')}
            {...form.getInputProps('label')}
          />
          <Select
            label={t('staff.metric')}
            data={enumChoices('currency_metric').map((choice) => ({
              value: choice.value,
              label: choice.label,
            }))}
            allowDeselect={false}
            {...form.getInputProps('metric')}
          />
          <Group grow align="flex-start">
            <Select
              label={t('staff.comparison')}
              data={enumChoices('currency_comparison').map((choice) => ({
                value: choice.value,
                label: choice.label,
              }))}
              allowDeselect={false}
              {...form.getInputProps('comparison')}
            />
            <NumberInput
              label={t('staff.threshold')}
              min={0}
              decimalScale={2}
              {...form.getInputProps('threshold')}
            />
            <NumberInput
              label={t('staff.warnMargin')}
              min={0}
              decimalScale={2}
              {...form.getInputProps('warn_margin')}
            />
          </Group>
          <Group grow align="flex-start">
            <Select
              label={t('settings.discipline')}
              data={enumChoices('discipline').map((choice) => ({
                value: choice.value,
                label: choice.label,
              }))}
              clearable
              {...form.getInputProps('discipline')}
            />
            <Select
              label={t('aircraft.category')}
              data={enumChoices('aircraft_category').map((choice) => ({
                value: choice.value,
                label: choice.label,
              }))}
              clearable
              {...form.getInputProps('aircraft_category')}
            />
          </Group>
          <Checkbox
            label={t('staff.blocksFlying')}
            {...form.getInputProps('blocks_flying', { type: 'checkbox' })}
          />
          <Checkbox
            label={t('common.active')}
            {...form.getInputProps('is_active', { type: 'checkbox' })}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('action.cancel')}
            </Button>
            <Button type="submit" loading={save.isPending}>
              {t('action.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
